import { useEffect, useLayoutEffect } from 'react'

import { getShellContentScrollElement } from 'shared/shell_content_scroll_registry'

/**
 * Scroll helpers for the event-type editor inside `#shell-content-scroll`.
 *
 * Focusing controls used to jump the outer shell into empty white ("half
 * screen") because preview layout inflated scroll height and focus helpers
 * re-called scrollIntoView. Layout caps sticky nav/preview to the scrollport so
 * the row is never sized by preview content; this hook freezes scroll across
 * keyboard focus moves and never calls scrollIntoView.
 */

/** Live client height of `#shell-content-scroll` — sticky preview must not exceed it. */
export const SHELL_SCROLL_CLIENT_HEIGHT_CSS_VAR = '--shell-content-scroll-client-height'

/** Live height of the sticky editor publish bar — offsets left nav / preview below it. */
export const EDITOR_HEADER_HEIGHT_CSS_VAR = '--et-crud-editor-header-height'

export const captureScrollPositions = (fromNode) => {
  const positions = []
  let node = fromNode

  while (node && node !== document.documentElement) {
    if (node.scrollHeight > node.clientHeight) {
      positions.push({ node, top: node.scrollTop, left: node.scrollLeft })
    }
    node = node.parentElement
  }

  return positions
}

export const restoreScrollPositions = (positions) => {
  positions.forEach(({ node, top, left }) => {
    if (!node.isConnected) return
    node.scrollTop = top
    node.scrollLeft = left
  })
}

const RADIO_FOCUS_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  ' ',
])

/**
 * Publish `#shell-content-scroll` client height as a CSS var so sticky columns
 * can use `max-height: var(--shell-content-scroll-client-height)` instead of
 * `100dvh` (taller than the scrollport once app chrome is subtracted).
 */
export const useSyncShellScrollClientHeightCssVar = () => {
  useLayoutEffect(() => {
    const scrollEl = getShellContentScrollElement()
    if (!scrollEl) return undefined

    const sync = () => {
      document.documentElement.style.setProperty(
        SHELL_SCROLL_CLIENT_HEIGHT_CSS_VAR,
        `${scrollEl.clientHeight}px`,
      )
    }

    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(scrollEl)
    window.addEventListener('resize', sync)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', sync)
      document.documentElement.style.removeProperty(SHELL_SCROLL_CLIENT_HEIGHT_CSS_VAR)
    }
  }, [])
}

/**
 * Publish the sticky editor header height so left nav and preview can stick
 * below the publish bar inside `#shell-content-scroll`.
 */
export const useSyncEditorHeaderHeightCssVar = (headerRef, enabled = true) => {
  useLayoutEffect(() => {
    if (!enabled) return undefined

    const headerEl = headerRef?.current
    if (!headerEl) return undefined

    const publishHeight = (height) => {
      document.documentElement.style.setProperty(
        EDITOR_HEADER_HEIGHT_CSS_VAR,
        `${height}px`,
      )
    }

    const sync = () => {
      publishHeight(headerEl.getBoundingClientRect().height)
    }

    sync()
    const observer = new ResizeObserver(([entry]) => {
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
      publishHeight(height)
    })
    observer.observe(headerEl)
    window.addEventListener('resize', sync)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', sync)
      document.documentElement.style.removeProperty(EDITOR_HEADER_HEIGHT_CSS_VAR)
    }
  }, [enabled, headerRef])
}

/**
 * Keep editor shell scroll stable across focus changes.
 *
 * - Pointer: label → radio uses `focus({ preventScroll: true })`
 * - Keyboard (Tab / radiogroup keys): restore scroll after focus moves
 * - Never call scrollIntoView (that was resizing the visible cut per control)
 */
export const usePreserveShellScrollOnFocus = (rootRef) => {
  useEffect(() => {
    const root = rootRef?.current
    if (!root) return undefined

    let savedPositions = null

    const onMouseDown = (event) => {
      const label = event.target?.closest?.('label')
      if (!label || !root.contains(label)) return
      const radio = label.querySelector('input[type="radio"]')
      if (!radio) return
      event.preventDefault()
      radio.focus({ preventScroll: true })
    }

    const onKeyDown = (event) => {
      const active = document.activeElement
      if (!active || !root.contains(active)) return

      if (event.key === 'Tab') {
        savedPositions = captureScrollPositions(active)
        return
      }

      if (!RADIO_FOCUS_KEYS.has(event.key)) return
      if (active.type !== 'radio') return
      savedPositions = captureScrollPositions(active)
    }

    const onFocusIn = (event) => {
      if (!savedPositions) return
      if (!root.contains(event.target)) {
        savedPositions = null
        return
      }

      const positions = savedPositions
      savedPositions = null

      requestAnimationFrame(() => {
        restoreScrollPositions(positions)
      })
    }

    root.addEventListener('mousedown', onMouseDown)
    root.addEventListener('keydown', onKeyDown, true)
    root.addEventListener('focusin', onFocusIn)

    return () => {
      root.removeEventListener('mousedown', onMouseDown)
      root.removeEventListener('keydown', onKeyDown, true)
      root.removeEventListener('focusin', onFocusIn)
    }
  }, [rootRef])
}
